import { Tooltip } from '@highlight/ui'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CalendarOff, ChevronDown, ChevronUp, Loader2, Users } from 'lucide-react'
import type { PreferredHourCycle } from '@shared/ipc/routers/BaseIpc.router'
import { formatUserTime } from '@renderer/overlay/Sidepanel/components/reminders/utils/user-time-format'
import {
  eventsForDay,
  packColumns,
  proposedSpanForDay,
  eventAvailabilitySpan,
  dayFraction,
  countConflicts,
  isSameLocalDay,
  intervalsOverlap,
  attendeeBusyBandsForDay,
  attendeeAvailabilityRows,
  type TimelineEvent,
  type PositionedEvent,
  type AttendeeFreeBusy,
  type AttendeeRow,
} from './day-timeline-layout'

interface DayTimelineProps {
  /** The local calendar day being edited (derived from the draft start). */
  day: Date
  /** The proposed event's start/end and all-day flag. */
  start: Date
  end: Date
  allDay: boolean
  /** Other events on (or spilling into) this day. */
  events: Array<TimelineEvent>
  hourCycle: PreferredHourCycle | null
  /** While a save is in flight the block is read-only (no drag). */
  disabled?: boolean
  /** Drag the block body: move start (preserves duration). */
  onMove?: (nextStart: Date) => void
  /** Drag the top edge: change start, keep end. */
  onResizeStart?: (nextStart: Date) => void
  /** Drag the bottom edge: change end, keep start. */
  onResizeEnd?: (nextEnd: Date) => void
  /**
   * Per-attendee FREE/BUSY for this day ("Phase 2"). When the event has no
   * attendees this is empty and no availability section renders. Each entry's
   * `status` is busy / free / unknown; `unknown` renders distinctly and is
   * excluded from the "everyone free / N conflicts" headline.
   */
  attendees?: Array<AttendeeFreeBusy>
  /** Resolved display names keyed by lowercased email, for the availability rows
   *  (falls back to the email when a guest has no resolved name). */
  guestNames?: Map<string, string>
  /** Free/busy fetch in flight (shows a subtle loading hint). */
  freeBusyLoading?: boolean
  /** Connection lacks the broad calendar scope → prompt a reconnect. */
  reconnectRequired?: boolean
}

/** Total pixel height of the 24h column. ~5.4px/hour keeps the popover compact. */
const DAY_HEIGHT = 560
/** Visible scroll viewport height. */
const VIEWPORT_HEIGHT = 300
/** Minimum rendered block height so short events stay readable/tappable. */
const MIN_BLOCK_HEIGHT = 16
/** Left gutter reserved for hour labels. */
const GUTTER = 34
/** Drag snap granularity, in minutes. */
const SNAP_MIN = 15
/** Px height of each resize handle hit zone at the block's top/bottom edge. */
const HANDLE_PX = 8
/** Below this rendered height a block is move-only (no room for edge handles). */
const MIN_RESIZE_BLOCK_PX = 24

const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => h)

const rangeDateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

/** "Wed, Jun 3" — shared between the single-day header and the range header. */
function formatRangeDate(date: Date): string {
  return rangeDateFormatter.format(date)
}

/** "Jun 4" — terse continuation-marker label. */
function formatMarkerDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
}

/** Format an hour-of-day (0–23) as a terse gutter label, honoring hour cycle. */
function formatHourLabel(hour: number, hourCycle: PreferredHourCycle | null): string {
  const uses24 = hourCycle === 'h23' || hourCycle === 'h24'
  if (uses24) return String(hour).padStart(2, '0')
  // 12h: compact "9a", "12p", "5p".
  const period = hour < 12 ? 'a' : 'p'
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12}${period}`
}

type DragMode = 'move' | 'resize-start' | 'resize-end'

/** In-flight drag state. Captured on pointerdown, cleared on pointerup. */
interface DragState {
  mode: DragMode
  pointerId: number
  startY: number
  /** The start/end Dates at the moment the drag began. */
  capturedStart: Date
  capturedEnd: Date
  /** Live preview start/end while dragging (snapped, clamped). */
  previewStart: Date
  previewEnd: Date
}

export function DayTimeline({
  day,
  start,
  end,
  allDay,
  events,
  hourCycle,
  disabled,
  onMove,
  onResizeStart,
  onResizeEnd,
  attendees = [],
  guestNames,
  freeBusyLoading = false,
  reconnectRequired = false,
}: DayTimelineProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  // Mirror the drag in a ref so the pointer handlers read the CURRENT gesture
  // synchronously. Reading it back through a setState updater is async — the
  // updater runs on the next render, after pointerup has already returned — which
  // dropped the commit entirely, so a drag never updated the time.
  const dragRef = useRef<DragState | null>(null)
  const setDragState = useCallback((next: DragState | null) => {
    dragRef.current = next
    setDrag(next)
  }, [])

  // Multi-day awareness (Fix C). Timed events only — an all-day event is shown
  // as a header pill, not a timed band.
  const endsLater = !allDay && !isSameLocalDay(end, day)
  const startsEarlier = !allDay && !isSameLocalDay(start, day)

  // While dragging, render the block from the local preview so it tracks the
  // pointer without committing (which would PATCH the backend per move).
  const renderStart = drag ? drag.previewStart : start
  const renderEnd = drag ? drag.previewEnd : end

  const proposed = useMemo(
    () => (allDay ? null : proposedSpanForDay(day, renderStart, renderEnd)),
    [day, renderStart, renderEnd, allDay],
  )

  // The span attendee availability is judged against — the WHOLE COMMITTED
  // proposed window (`[start, end)`, or the full day-range for all-day), NOT
  // clamped to the day this timeline draws (a draft can straddle days, e.g.
  // 11pm→1am). Crucially this uses the COMMITTED start/end, not the live drag
  // preview: the free/busy data is fetched and cached against the committed
  // window (use-attendee-freebusy keys on it), so judging a mid-drag preview that
  // extends past that window (e.g. dragging the end past midnight) against data
  // we never fetched would mislead. Attendee verdicts therefore update on commit
  // (which refetches); the blue block still tracks the drag live.
  const availabilitySpan = useMemo(() => eventAvailabilitySpan(start, end, allDay), [start, end, allDay])

  // The committed proposed slot clamped to THIS day, for coloring attendee bands
  // as conflicts. Committed (not the drag preview) so the band colour always
  // agrees with the footer verdict, which is also committed.
  const committedProposed = useMemo(
    () => (allDay ? null : proposedSpanForDay(day, start, end)),
    [day, start, end, allDay],
  )

  const positioned = useMemo(() => {
    const spans = eventsForDay(events, day)
    return packColumns(spans, proposed)
  }, [events, day, proposed])

  const conflictCount = useMemo(() => countConflicts(positioned), [positioned])
  const hasEvents = positioned.length > 0

  const hasAttendees = attendees.length > 0

  // Attendee busy bands, clamped to this day. Drawn as a thin strip on the right
  // edge of the lane so they read as a separate signal from the user's own
  // event blocks. `unknown` attendees contribute no bands (we don't know their
  // schedule) — they're surfaced only in the availability summary.
  const attendeeBands = useMemo(
    () => (allDay ? [] : attendees.flatMap((a) => attendeeBusyBandsForDay(a, day))),
    [attendees, day, allDay],
  )

  // Per-attendee availability rows, framed around the proposed slot (busy = can't
  // make it / free = can / unknown). The footer renders these directly so the
  // user sees WHO is free or busy, and an `unknown` attendee never implies we can
  // see a calendar we can't.
  const availabilityRows = useMemo(
    () => attendeeAvailabilityRows(attendees, availabilitySpan),
    [attendees, availabilitySpan],
  )

  // Auto-center the proposed slot (or the first event when all-day) in the
  // viewport on mount and whenever the proposed slot moves. Frozen during a drag
  // so the column doesn't scroll out from under the pointer.
  const focusFraction = useMemo(() => {
    if (proposed) return dayFraction(day, (proposed.startMs + proposed.endMs) / 2)
    if (hasEvents) return dayFraction(day, positioned[0].startMs)
    return 9 / 24 // default to morning if nothing to anchor on
  }, [proposed, hasEvents, positioned, day])

  useLayoutEffect(() => {
    if (drag) return
    const el = scrollRef.current
    if (!el) return
    const target = focusFraction * DAY_HEIGHT - VIEWPORT_HEIGHT / 2
    el.scrollTop = Math.max(0, Math.min(target, DAY_HEIGHT - VIEWPORT_HEIGHT))
  }, [focusFraction, drag])

  const proposedTop = proposed ? dayFraction(day, proposed.startMs) * DAY_HEIGHT : 0
  const proposedHeight = proposed
    ? Math.max(MIN_BLOCK_HEIGHT, (dayFraction(day, proposed.endMs) - dayFraction(day, proposed.startMs)) * DAY_HEIGHT)
    : 0

  const interactive = !allDay && !disabled && !!(onMove || onResizeStart || onResizeEnd)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, mode: DragMode) => {
      // Each hit zone only renders when its handler exists, so by the time we get
      // here `mode` is always actionable.
      if (!interactive) return
      e.preventDefault()
      e.stopPropagation()
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      setDragState({
        mode,
        pointerId: e.pointerId,
        startY: e.clientY,
        capturedStart: start,
        capturedEnd: end,
        previewStart: start,
        previewEnd: end,
      })
    },
    [interactive, start, end, setDragState],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const prev = dragRef.current
      if (!prev || e.pointerId !== prev.pointerId) return
      const deltaMin = Math.round((((e.clientY - prev.startY) / DAY_HEIGHT) * 1440) / SNAP_MIN) * SNAP_MIN
      const dayStart = new Date(prev.capturedStart)
      dayStart.setHours(0, 0, 0, 0)
      const dayStartMs = dayStart.getTime()
      const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000
      const minMs = SNAP_MIN * 60 * 1000

      if (prev.mode === 'move') {
        const duration = prev.capturedEnd.getTime() - prev.capturedStart.getTime()
        let nextStartMs = prev.capturedStart.getTime() + deltaMin * 60 * 1000
        // Keep the START on the edited day — cap at the last snap slot before
        // midnight (`dayEndMs - minMs`), not midnight itself (which is the next
        // day and would make the band vanish from the column). The end may still
        // legitimately spill past midnight for a multi-day event.
        nextStartMs = Math.max(dayStartMs, Math.min(nextStartMs, dayEndMs - minMs))
        setDragState({ ...prev, previewStart: new Date(nextStartMs), previewEnd: new Date(nextStartMs + duration) })
        return
      }

      if (prev.mode === 'resize-start') {
        let nextStartMs = prev.capturedStart.getTime() + deltaMin * 60 * 1000
        const maxStartMs = prev.capturedEnd.getTime() - minMs
        nextStartMs = Math.max(dayStartMs, Math.min(nextStartMs, maxStartMs))
        setDragState({ ...prev, previewStart: new Date(nextStartMs) })
        return
      }

      // resize-end — clamp only the lower bound (end >= start + min). The end may
      // legitimately fall on a later calendar day (multi-day), so we must NOT cap
      // it at midnight of the start day: doing so would yank an overnight event's
      // end down to midnight on the first pointer move and commit a shorter event.
      let nextEndMs = prev.capturedEnd.getTime() + deltaMin * 60 * 1000
      const minEndMs = prev.capturedStart.getTime() + minMs
      nextEndMs = Math.max(minEndMs, nextEndMs)
      setDragState({ ...prev, previewEnd: new Date(nextEndMs) })
    },
    [setDragState],
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Read the CURRENT gesture synchronously from the ref (a setState updater
      // would run too late — after this handler returns), then clear it and commit
      // ONCE, here in the event handler (not in a React updater, so StrictMode can't
      // double-invoke it).
      const gesture = dragRef.current
      if (!gesture || e.pointerId !== gesture.pointerId) return
      setDragState(null)
      const el = e.currentTarget as HTMLElement
      if (el.hasPointerCapture(gesture.pointerId)) el.releasePointerCapture(gesture.pointerId)
      // Commit only when the gesture actually moved (a click with no movement leaves
      // preview === captured → no needless PATCH).
      if (gesture.mode === 'move') {
        if (gesture.previewStart.getTime() !== gesture.capturedStart.getTime()) onMove?.(gesture.previewStart)
      } else if (gesture.mode === 'resize-start') {
        if (gesture.previewStart.getTime() !== gesture.capturedStart.getTime()) onResizeStart?.(gesture.previewStart)
      } else {
        if (gesture.previewEnd.getTime() !== gesture.capturedEnd.getTime()) onResizeEnd?.(gesture.previewEnd)
      }
    },
    [onMove, onResizeStart, onResizeEnd, setDragState],
  )

  // Short blocks can't host distinct edge handles — fall back to move-only.
  const allowEdgeHandles = interactive && proposedHeight >= MIN_RESIZE_BLOCK_PX
  const dragging = drag !== null
  const blockCursor = dragging ? 'cursor-grabbing' : 'cursor-grab'

  return (
    <div className="flex w-[148px] shrink-0 flex-col border-r pr-2.5">
      {/* Header: day (or multi-day range) + conflict / context summary */}
      <div className="mb-1.5 flex items-baseline justify-between gap-1">
        <span className="text-foreground-secondary min-w-0 truncate text-[11px] font-semibold">
          {endsLater ? (
            <>
              {formatRangeDate(day)} <span className="text-foreground-disabled">→ {formatRangeDate(renderEnd)}</span>
            </>
          ) : (
            formatRangeDate(day)
          )}
        </span>
        {allDay ? (
          <span className="text-foreground-disabled shrink-0 text-[10px] font-medium">All day</span>
        ) : conflictCount > 0 ? (
          <span className="text-warning-subtle shrink-0 text-[10px] font-semibold">
            {conflictCount} conflict{conflictCount > 1 ? 's' : ''}
          </span>
        ) : hasEvents ? (
          <span className="text-foreground-disabled shrink-0 text-[10px] font-medium">clear</span>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="scrollbar-hide relative overflow-y-auto overscroll-contain rounded-md"
        style={{ height: VIEWPORT_HEIGHT }}
      >
        <div className="relative" style={{ height: DAY_HEIGHT }}>
          {/* Hour gridlines + labels */}
          {HOUR_LABELS.map((hour) => {
            const top = (hour / 24) * DAY_HEIGHT
            return (
              <div key={hour} className="pointer-events-none absolute inset-x-0" style={{ top }}>
                <div className="absolute inset-x-0 border-t" style={{ left: GUTTER }} />
                <span className="text-foreground-disabled absolute -translate-y-1/2 text-[9px] font-medium tabular-nums">
                  {formatHourLabel(hour, hourCycle)}
                </span>
              </div>
            )
          })}

          {/* Proposed slot band (timed only) */}
          {proposed && (
            <div
              className="border-info bg-info-subtle-hover absolute z-20 select-none rounded-[5px] border shadow-[0_0_0_1px_rgba(59,130,246,0.25)]"
              style={{
                top: proposedTop,
                height: proposedHeight,
                left: GUTTER + 1,
                right: 1,
                touchAction: interactive ? 'none' : undefined,
              }}
            >
              {/* Move zone (whole block, or just the middle when handles fit). */}
              {interactive && onMove && (
                <div
                  className={`absolute inset-x-0 ${blockCursor}`}
                  style={{
                    top: allowEdgeHandles ? HANDLE_PX : 0,
                    bottom: allowEdgeHandles ? HANDLE_PX : 0,
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => handlePointerDown(e, 'move')}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />
              )}

              {/* Top resize handle (resize-start). */}
              {allowEdgeHandles && onResizeStart && (
                <div
                  className="absolute inset-x-0 top-0 cursor-ns-resize"
                  style={{ height: HANDLE_PX, touchAction: 'none' }}
                  onPointerDown={(e) => handlePointerDown(e, 'resize-start')}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />
              )}

              {/* Bottom resize handle (resize-end). */}
              {allowEdgeHandles && onResizeEnd && (
                <div
                  className="absolute inset-x-0 bottom-0 cursor-ns-resize"
                  style={{ height: HANDLE_PX, touchAction: 'none' }}
                  onPointerDown={(e) => handlePointerDown(e, 'resize-end')}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />
              )}

              {/* Top continuation marker: event started on an earlier day. */}
              {startsEarlier && (
                <div className="text-info pointer-events-none absolute inset-x-1 top-0 flex items-center gap-0.5 text-[8px] font-medium leading-none">
                  <ChevronUp className="size-2.5 shrink-0" />
                  <span className="truncate">{formatMarkerDate(renderStart)}</span>
                </div>
              )}

              {/* Start-time label. Nudged down when a top marker is present. */}
              <div
                className={`text-info pointer-events-none absolute inset-x-1 truncate text-[9px] font-semibold leading-tight ${
                  startsEarlier ? 'top-2.5' : 'top-0.5'
                }`}
              >
                {formatUserTime(renderStart, hourCycle)}
              </div>

              {/* Bottom continuation marker: event ends on a later day. */}
              {endsLater && (
                <div className="text-info pointer-events-none absolute inset-x-1 bottom-0 flex items-center gap-0.5 text-[8px] font-medium leading-none">
                  <ChevronDown className="size-2.5 shrink-0" />
                  <span className="truncate">continues → {formatMarkerDate(renderEnd)}</span>
                </div>
              )}
            </div>
          )}

          {/* Existing event blocks */}
          {positioned.map((p) => (
            <EventBlock key={p.event.id} positioned={p} day={day} hourCycle={hourCycle} />
          ))}

          {/* Attendee busy strip — a thin column on the right edge, separate from
              the user's own event blocks. A band that overlaps the proposed slot
              is a real CONFLICT (amber, matching the "N busy" headline); a band
              elsewhere that day is just context (violet). So the colour a user
              sees on the timeline always agrees with the verdict in the footer. */}
          {hasAttendees &&
            !allDay &&
            attendeeBands.map((band, i) => {
              const top = dayFraction(day, band.startMs) * DAY_HEIGHT
              const height = Math.max(3, (dayFraction(day, band.endMs) - dayFraction(day, band.startMs)) * DAY_HEIGHT)
              const conflicts =
                !!committedProposed &&
                intervalsOverlap(band.startMs, band.endMs, committedProposed.startMs, committedProposed.endMs)
              const who = guestNames?.get(band.email.toLowerCase()) ?? band.email
              const when = `${formatUserTime(new Date(band.startMs), hourCycle)} – ${formatUserTime(new Date(band.endMs), hourCycle)}`
              return (
                // The visible band is a thin 5px sliver, but a native `title`
                // tooltip doesn't render in the overlay window — use the app
                // Tooltip and a wider (10px) transparent hit area so hovering to
                // see "who · when" actually works.
                <Tooltip key={`${band.email}-${i}`}>
                  <Tooltip.Trigger
                    render={
                      <div className="absolute z-[15] flex justify-end" style={{ top, height, right: 1, width: 10 }} />
                    }
                  >
                    <div className={`h-full w-[5px] rounded-[2px] ${conflicts ? 'bg-warning' : 'bg-purple'}`} />
                  </Tooltip.Trigger>
                  <Tooltip.Content side="left">{`${who} · busy ${when}`}</Tooltip.Content>
                </Tooltip>
              )
            })}
        </div>
      </div>

      {/* Empty / context footer */}
      {!hasEvents && (
        <div className="text-foreground-disabled mt-1.5 flex items-center gap-1.5 text-[10px] font-medium">
          <CalendarOff className="size-3" />
          No other events
        </div>
      )}

      {/* Attendee availability — when the event has guests, or while a free/busy
          fetch is still in flight (attendees is empty until it returns, so we
          also gate on the loading flag to surface "Checking availability…"). */}
      {(hasAttendees || freeBusyLoading) && (
        <AttendeeAvailability
          rows={availabilityRows}
          guestNames={guestNames}
          loading={freeBusyLoading}
          reconnectRequired={reconnectRequired}
        />
      )}
    </div>
  )
}

/** Max attendee rows shown before collapsing the remainder into "+N more". */
const MAX_ATTENDEE_ROWS = 6

/** Friendly label for a guest — the resolved name, else the email's local part. */
function guestLabel(email: string, names?: Map<string, string>): string {
  return names?.get(email.toLowerCase()) ?? email.split('@')[0] ?? email
}

/**
 * The attendee availability footer. A one-line verdict for the PROPOSED SLOT
 * ("Everyone's free" / "N busy" / "Availability unknown") followed by a row per
 * guest showing whether THEY can make this time — free / busy / unknown — plus a
 * muted "busy elsewhere" note for guests who are free now but have other
 * commitments today. Every guest is listed (capped, then "+N more"), so the
 * count and the bands always have a who behind them. When the connection lacks
 * the broad calendar scope we prompt a reconnect instead of a misleading claim.
 */
function AttendeeAvailability({
  rows,
  guestNames,
  loading,
  reconnectRequired,
}: {
  rows: Array<AttendeeRow>
  guestNames?: Map<string, string>
  loading: boolean
  reconnectRequired: boolean
}) {
  const knownCount = rows.filter((r) => r.slot !== 'unknown').length
  const busyCount = rows.filter((r) => r.slot === 'busy').length
  const everyoneFree = knownCount > 0 && busyCount === 0
  const shown = rows.slice(0, MAX_ATTENDEE_ROWS)
  const overflow = rows.length - shown.length

  return (
    <div className="mt-1.5 flex flex-col gap-1.5 border-t pt-1.5">
      {loading ? (
        <div className="text-foreground-disabled flex items-center gap-1.5 text-[10px] font-medium">
          <Loader2 className="size-3 animate-spin" />
          Checking availability…
        </div>
      ) : reconnectRequired ? (
        <div className="text-warning-subtle flex items-center gap-1.5 text-[10px] font-medium">
          <Users className="size-3 shrink-0" />
          <span>Availability unknown — reconnect calendar</span>
        </div>
      ) : (
        <>
          {/* Verdict for the proposed slot. */}
          <div className="flex items-center gap-1.5 text-[10px] font-semibold">
            <Users className="text-foreground-placeholder size-3 shrink-0" />
            {knownCount === 0 ? (
              <span className="text-foreground-placeholder">Availability unknown</span>
            ) : everyoneFree ? (
              <span className="text-success-subtle">Everyone's free</span>
            ) : (
              <span className="text-warning-subtle">{busyCount} busy at this time</span>
            )}
          </div>

          {/* One row per guest: can THEY make this slot? */}
          <div className="flex flex-col gap-1">
            {shown.map((row) => (
              <AttendeeRowItem key={row.email} row={row} label={guestLabel(row.email, guestNames)} />
            ))}
            {overflow > 0 && (
              <div className="text-foreground-disabled pl-3 text-[9px] font-medium">+{overflow} more</div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * A single guest row: a status dot + name + their verdict FOR THE PROPOSED SLOT
 * (Free / Busy / Unknown). Whether they're busy at OTHER times isn't stated here
 * — it's just noise for the scheduling decision; the lilac bands on the timeline
 * show those periods, and hovering a band reveals who is busy and when.
 */
function AttendeeRowItem({ row, label }: { row: AttendeeRow; label: string }) {
  const dot =
    row.slot === 'busy'
      ? 'bg-warning'
      : row.slot === 'free'
        ? 'bg-success'
        : 'bg-[repeating-linear-gradient(45deg,rgba(148,148,148,0.6),rgba(148,148,148,0.6)_1px,transparent_1px,transparent_3px)]'
  const verdict =
    row.slot === 'busy'
      ? { text: 'Busy', className: 'text-warning-subtle' }
      : row.slot === 'free'
        ? { text: 'Free', className: 'text-success-subtle' }
        : { text: 'Unknown', className: 'text-foreground-placeholder' }

  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <span className={`size-2 shrink-0 rounded-full ${dot}`} aria-hidden />
      <span className="text-foreground-secondary min-w-0 flex-1 truncate font-medium" title={label}>
        {label}
      </span>
      <span className={`shrink-0 font-semibold ${verdict.className}`}>{verdict.text}</span>
    </div>
  )
}

function EventBlock({
  positioned,
  day,
  hourCycle,
}: {
  positioned: PositionedEvent
  day: Date
  hourCycle: PreferredHourCycle | null
}) {
  const { event, startMs, endMs, column, columnCount, conflict } = positioned
  const top = dayFraction(day, startMs) * DAY_HEIGHT
  const rawHeight = (dayFraction(day, endMs) - dayFraction(day, startMs)) * DAY_HEIGHT
  const height = Math.max(MIN_BLOCK_HEIGHT, rawHeight)

  // Column tiling within the event lane (right of the gutter). 1px inter-column gap.
  const laneLeft = GUTTER + 1
  const laneWidth = `calc(100% - ${laneLeft + 1}px)`
  const widthExpr = `calc((${laneWidth} - ${(columnCount - 1) * 2}px) / ${columnCount})`
  const leftExpr = `calc(${laneLeft}px + (${widthExpr} + 2px) * ${column})`

  const compact = height < 26

  return (
    <div
      className={`absolute z-10 overflow-hidden rounded-[4px] border px-1 leading-tight transition-colors ${
        conflict
          ? 'border-warning bg-warning-subtle text-warning'
          : 'border-border bg-foreground/[0.07] text-foreground-secondary'
      }`}
      style={{ top, height, left: leftExpr, width: widthExpr }}
      title={`${event.title} · ${formatUserTime(event.start, hourCycle)}`}
    >
      <div className={`truncate font-semibold ${compact ? 'text-[9px] leading-[14px]' : 'text-[10px]'}`}>
        {event.title || 'Busy'}
      </div>
      {!compact && (
        <div className={`truncate text-[9px] ${conflict ? 'text-warning-subtle' : 'text-foreground-placeholder'}`}>
          {formatUserTime(event.start, hourCycle)}
        </div>
      )}
    </div>
  )
}
